/**
 * @fileoverview A function that formats the participant status.
 *
 * @example
 * formatParticipantStatus('complete', 100, 'A', '2024-01-01')
 */

import { DateTime, Interval } from 'luxon'
import { formatScore } from './formatScore'

const statusMapStyle: Partial<Record<Novoic.AssignmentStatus, string>> = {
    empty: 'bg-indigo-100 text-indigo-800',
    overdue: 'bg-yellow-100 text-yellow-800',
    not_started: 'bg-gray-100 text-gray-800',
    incomplete: 'bg-gray-100 text-gray-800',
    assigned: 'bg-indigo-100 text-indigo-800',
    complete: 'bg-indigo-500 text-white',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-red-100 text-red-800',
    timeout: 'bg-red-100 text-red-800',
    not_eligible: 'bg-red-100 text-red-800',
}

const statusMap: Record<string, string> = {
    empty: 'No test',
    not_started: 'Pending',
    incomplete: 'Pending',
    assigned: 'Pending',
    complete: 'Ready',
    cancelled: 'Failed',
    expired: 'Failed',
    timeout: 'Failed',
    not_eligible: 'Not eligible',
}

/**
 * @function classNames
 * @description Joins an array of class names into a single string.
 * @param {string[]} classes - The array of class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function getLabel
 * @description Gets the label for the participant status.
 * @param {Novoic.AssignmentStatus} status - The status of the participant.
 * @param {string} title - The title of the participant status.
 * @param {string} desc - The description of the participant status.
 * @returns {JSX.Element} - The label for the participant status.
 */
function getLabel(status: Novoic.AssignmentStatus, title: string, desc?: string) {
    return (
        <>
            <span
                className={classNames(
                    'text-xs font-medium rounded-[10px] py-0.5 px-2',
                    statusMapStyle[status]
                )}
            >
                {title || statusMap[status]}
            </span>
            {desc && <span className="pl-1 text-sm font-medium">{desc}</span>}
        </>
    )
}

/**
 * @function formatParticipantStatus
 * @description Formats the participant status.
 * @param {Novoic.AssignmentStatus} status - The status of the participant.
 * @param {number} score - The score of the participant.
 * @param {string} level - The level of the participant.
 * @param {string} timestamp - The timestamp of the participant.
 * @returns {JSX.Element} - The formatted participant status.
 */
export function formatParticipantStatus(
    status: Novoic.AssignmentStatus,
    score: number,
    level: string,
    timestamp: string
): JSX.Element {
    if (!status) {
        status = 'not_started'
    }

    let formatted = statusMap[status]
    const dt = DateTime.fromISO(timestamp)
    let desc = formatted
    let label = getLabel(status, formatted)

    if (status === 'complete') {
        return (
            <div className="flex flex-col gap-y-1">
                {formatScore(score, level)}
                {timestamp && (
                    <span className="text-gray-600 text-xs font-normal">{`Completed ${dt.toRelative()}`}</span>
                )}
            </div>
        )
    }

    if (formatted === 'Pending') {
        desc = 'Assigned'
        if (Interval.fromDateTimes(dt, DateTime.now()).length('hours') > 48) {
            formatted = 'Overdue'
            status = 'overdue'
            label = getLabel(status, formatted)
        }
    } else if (formatted === 'Ready') {
        label = getLabel(status, `${Math.round(score)}`, level)
        desc = 'Completed'
    }

    if (status === 'not_eligible' || status === 'timeout' || status === 'expired') {
        desc = ''
    }

    return (
        <div className="flex flex-col gap-y-1">
            <div>{label}</div>
            {timestamp && (
                <span className="text-gray-600 text-xs font-normal">{`${desc} ${dt.toRelative()}`}</span>
            )}
        </div>
    )
}
